import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { LogoIcon } from '../Logo';
import { Loader2, Gift } from 'lucide-react';
import { api } from '../../lib/api';
import { FestiveDecoration } from '../FestiveDecoration';
import { toast } from 'sonner';

interface RegisterScreenProps {
  contact: string;
  onSuccess: () => void;
}

export function RegisterScreen({ contact, onSuccess }: RegisterScreenProps) {
  const [name, setName] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    // Birthdate is optional, but if provided, both month and day are needed
    const birthdate = birthMonth && birthDay ? `${birthMonth}-${birthDay}` : null;

    setIsLoading(true);

    try {
      const response = await api.register(contact, birthdate, name);
      
      if (response.success) {
        toast.success('Welcome to BtGtR! 🎉', {
          description: 'Your account has been created successfully!',
        });
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setError(errorMessage);
      toast.error('Registration failed', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center p-4 relative overflow-hidden">
      <FestiveDecoration />
      
      <Card className="w-full max-w-md border-2 border-emerald-100 shadow-xl relative z-10">
        <CardHeader className="text-center space-y-4 pb-6">
          <div className="flex justify-center mb-2">
            <LogoIcon size="lg" />
          </div>
          <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
            <Gift className="w-8 h-8 text-emerald-600" />
          </div>
          <CardTitle className="text-2xl">Complete Your Profile 🎉</CardTitle>
          <CardDescription className="text-base">
            Tell us a bit about yourself to get started!
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Your Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                placeholder="John Doe"
                disabled={isLoading}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Your Birthdate
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <select
                    id="birthMonth"
                    value={birthMonth}
                    onChange={(e) => {
                      setBirthMonth(e.target.value);
                      setError('');
                    }}
                    disabled={isLoading}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Month</option>
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div>
                  <select
                    id="birthDay"
                    value={birthDay}
                    onChange={(e) => {
                      setBirthDay(e.target.value);
                      setError('');
                    }}
                    disabled={isLoading}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day.toString().padStart(2, '0')}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Your Account...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Complete Registration
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t">
            <p className="text-xs text-center text-muted-foreground">
              Your information is private and secure.
            </p>
            <p className="text-xs text-center text-muted-foreground mt-2">
              By continuing, you agree to our{' '}
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 underline"
              >
                Terms and Conditions
              </a>
              .
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}